import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { confirmDialog } from "../turbo_confirm"
import { buildCaptionInput } from "../helpers/caption_input"
import { TIMESHEET_DATA_TYPE } from "../helpers/timesheet_data_transfer"

export default class extends Controller {
  static targets = ["input", "dropzone", "fileList", "pendingList", "emptyState"]

  static values = {
    maxSize: { type: Number, default: 104857600 }, // 100MB
    acceptedTypes: {
      type: String,
      default: "image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md",
    },
    fieldId: String,
    directUploadUrl: String,
    errorSessionExpired: String,
    errorNetwork: String,
    errorFileRead: String,
    captionPlaceholder: String,
    captionMaxLength: Number,
  }

  connect() {
    this.uploadedBlobs = new Map()
    this.updateEmptyState()
  }

  // Drag & drop handlers. A drag from the "Daten aus Stundenzetteln" panel carries
  // TIMESHEET_DATA_TYPE instead of files; it is left to bubble up to the panel's
  // controller (timesheet_data_panel_controller.js), which takes it over.
  dragover(event) {
    if (this.fromTimesheetPanel(event)) return

    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add("border-green-500", "bg-green-50")
  }

  dragleave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-green-500", "bg-green-50")
  }

  drop(event) {
    if (this.fromTimesheetPanel(event)) return

    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-green-500", "bg-green-50")

    const files = event.dataTransfer.files
    Array.from(files).forEach((file) => this.uploadFile(file))
  }

  // File input change handler
  selectFiles(event) {
    const files = event.target.files
    Array.from(files).forEach((file) => this.uploadFile(file))
    // Reset input so same file can be selected again
    event.target.value = null
  }

  // A voice note recorded in the browser (audio_recorder_controller.js, Audio field): uploaded
  // exactly like a picked file.
  uploadRecorded(event) {
    const file = event.detail?.file
    if (file) this.uploadFile(file)
  }

  // Upload file using DirectUpload
  uploadFile(file) {
    // Validate file size
    if (file.size > this.maxSizeValue) {
      alert(`${file.name} ist zu groß. Maximum: ${this.formatFileSize(this.maxSizeValue)}`)
      return
    }

    // Create UI element for this upload
    const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const element = this.createUploadingElement(uploadId, file)
    this.pendingListTarget.appendChild(element)
    this.updateEmptyState()

    // Start the direct upload
    const upload = new DirectUpload(file, this.directUploadUrlValue, this)

    upload.create((error, blob) => {
      if (error) {
        console.error("Upload error:", error)
        this.showUploadError(uploadId, error)
      } else {
        this.completeUpload(uploadId, blob, file)
      }
    })
  }

  // DirectUpload delegate method for progress
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", (event) => {
      const progress = (event.loaded / event.total) * 100
      // Find the progress bar for this upload and update it
      const progressBars = this.pendingListTarget.querySelectorAll("[data-progress-bar]")
      progressBars.forEach((bar) => {
        bar.style.width = `${progress}%`
      })
    })
  }

  // Create DOM element for uploading file
  createUploadingElement(id, file) {
    const div = document.createElement("div")
    div.id = id
    div.className =
      "flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg"

    const isImage = file.type.startsWith("image/")
    const icon = this.getFileIcon(file.type)

    div.innerHTML = `
      <div class="flex items-center space-x-3 min-w-0 flex-1">
        ${
          isImage
            ? `
          <img src="${URL.createObjectURL(file)}" class="h-10 w-10 object-cover rounded" alt="${file.name}">
        `
            : `
          <div class="h-10 w-10 flex items-center justify-center bg-gray-100 rounded">
            ${icon}
          </div>
        `
        }
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-gray-900 truncate">${file.name}</p>
          <p class="text-xs text-gray-500">${this.formatFileSize(file.size)}</p>
          <div class="mt-1 w-full bg-gray-200 rounded-full h-1.5">
            <div data-progress-bar class="bg-green-600 h-1.5 rounded-full transition-all duration-300" style="width: 0%"></div>
          </div>
        </div>
      </div>
      <div class="ml-2 flex items-center">
        <svg class="animate-spin h-5 w-5 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    `

    return div
  }

  // Complete upload - create hidden input with signed_id
  completeUpload(uploadId, blob, file) {
    const element = document.getElementById(uploadId)
    if (!element) return

    // Create hidden input for form submission
    const hiddenInput = document.createElement("input")
    hiddenInput.type = "hidden"
    hiddenInput.name = `field_attachments[${this.fieldIdValue}][signed_ids][]`
    hiddenInput.value = blob.signed_id
    hiddenInput.dataset.uploadId = uploadId
    this.element.appendChild(hiddenInput)

    // Store reference for removal
    this.uploadedBlobs.set(uploadId, { blob, input: hiddenInput, file })

    // Update UI to show completed state: the file line, its caption input below it
    element.className = "p-3 bg-green-50 border border-green-200 rounded-lg"

    const isImage = file.type.startsWith("image/")
    const icon = this.getFileIcon(file.type)

    element.innerHTML = `
      <div class="flex items-center justify-between">
      <div class="flex items-center space-x-3 min-w-0 flex-1">
        ${
          isImage
            ? `
          <img src="${URL.createObjectURL(file)}" class="h-10 w-10 object-cover rounded" alt="${file.name}">
        `
            : `
          <div class="h-10 w-10 flex items-center justify-center bg-gray-100 rounded">
            ${icon}
          </div>
        `
        }
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-gray-900 truncate">${file.name}</p>
          <p class="text-xs text-green-600">${this.formatFileSize(file.size)} - Hochgeladen</p>
        </div>
      </div>
      <button type="button"
              class="ml-2 p-1 text-gray-400 hover:text-red-500"
              data-action="click->file-upload#removeUpload"
              data-upload-id="${uploadId}">
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
      </div>
    `
    // The photo's description, posted with its signed id.
    element.append(
      buildCaptionInput({
        name: `field_attachments[${this.fieldIdValue}][captions][${blob.signed_id}]`,
        placeholder: this.captionPlaceholderValue,
        maxLength: this.captionMaxLengthValue,
      }),
    )
  }

  // Map the raw DirectUpload error (a plain string, not an Error object) to an
  // actionable message. The status codes come from @rails/activestorage's
  // "Error creating Blob …" / "Error storing …" strings. The translated
  // messages are passed in from the view; without them the raw error is shown.
  friendlyUploadError(raw) {
    if (/Status: 422/.test(raw)) {
      return this.errorSessionExpiredValue || raw
    }
    if (/Status: 0(\D|$)/.test(raw)) {
      return this.errorNetworkValue || raw
    }
    if (/Error reading/.test(raw)) {
      return this.errorFileReadValue || raw
    }
    return raw
  }

  // Show upload error
  showUploadError(uploadId, error) {
    const element = document.getElementById(uploadId)
    if (!element) return

    const raw = typeof error === "string" ? error : error?.message || String(error)
    const friendly = this.friendlyUploadError(raw)

    element.className =
      "flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg"

    element.innerHTML = `
      <div class="flex items-center space-x-3 min-w-0 flex-1">
        <div class="h-10 w-10 flex items-center justify-center bg-red-100 rounded">
          <svg class="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-red-800">Fehler beim Hochladen</p>
          <p data-error-friendly class="text-xs text-red-600"></p>
          ${friendly !== raw ? '<p data-error-raw class="text-xs text-red-400 truncate"></p>' : ""}
        </div>
      </div>
      <button type="button"
              class="ml-2 p-1 text-gray-400 hover:text-red-500"
              data-action="click->file-upload#removeErrorElement"
              data-upload-id="${uploadId}">
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    `

    // The error strings contain the client-side filename — assign as text, never markup
    element.querySelector("[data-error-friendly]").textContent = friendly
    const rawElement = element.querySelector("[data-error-raw]")
    if (rawElement) rawElement.textContent = raw
  }

  // Remove uploaded file (before form submit)
  removeUpload(event) {
    const uploadId = event.currentTarget.dataset.uploadId
    const blobInfo = this.uploadedBlobs.get(uploadId)

    if (blobInfo) {
      blobInfo.input.remove()
      this.uploadedBlobs.delete(uploadId)
    }

    this.removeRow(document.getElementById(uploadId))
  }

  // Removes a pending, staged or saved row. One that holds a copy of a timesheet photo
  // (`data-copied-source`) tells the "Daten aus Stundenzetteln" panel, which offers the
  // photo again — after the row is gone, since the panel reads the form.
  removeRow(row) {
    row?.remove()
    this.updateEmptyState()
    if (row?.dataset.copiedSource) {
      this.dispatch("copied-removed", { detail: { sourceId: row.dataset.copiedSource } })
    }
  }

  fromTimesheetPanel(event) {
    return Array.from(event.dataTransfer?.types || []).includes(TIMESHEET_DATA_TYPE)
  }

  // A photo taken over from the "Daten aus Stundenzetteln" panel. The server already
  // made an independent, unattached copy of it (CustomReportTimesheetDataController
  // #copy_upload), so it joins the pending uploads like a finished direct upload and
  // is saved with the report. `sources` tells the report which timesheet upload it
  // came from, so the panel shows it as taken over.
  addCopiedFile({ signedId, sourceId, filename, byteSize, caption, previewUrl, image }) {
    const uploadId = `copied-upload-${sourceId}-${Date.now()}`
    const hiddenInput = document.createElement("input")
    hiddenInput.type = "hidden"
    hiddenInput.name = `field_attachments[${this.fieldIdValue}][signed_ids][]`
    hiddenInput.value = signedId
    hiddenInput.dataset.uploadId = uploadId
    this.element.appendChild(hiddenInput)
    this.uploadedBlobs.set(uploadId, {
      blob: { signed_id: signedId },
      input: hiddenInput,
      file: null,
    })

    const row = document.createElement("div")
    row.id = uploadId
    row.dataset.copiedSource = String(sourceId)
    row.className = "p-3 bg-green-50 border border-green-200 rounded-lg"
    row.append(this.buildCopiedLine({ uploadId, filename, byteSize, previewUrl, image }))

    const source = document.createElement("input")
    source.type = "hidden"
    source.name = `field_attachments[${this.fieldIdValue}][sources][${signedId}]`
    source.value = String(sourceId)
    const captionInput = buildCaptionInput({
      name: `field_attachments[${this.fieldIdValue}][captions][${signedId}]`,
      placeholder: this.captionPlaceholderValue,
      maxLength: this.captionMaxLengthValue,
    })
    captionInput.value = caption || ""
    row.append(source, captionInput)

    this.pendingListTarget.appendChild(row)
    this.updateEmptyState()
  }

  buildCopiedLine({ uploadId, filename, byteSize, previewUrl, image }) {
    const line = document.createElement("div")
    line.className = "flex items-center justify-between"

    const info = document.createElement("div")
    info.className = "flex items-center space-x-3 min-w-0 flex-1"
    if (image && previewUrl) {
      const img = document.createElement("img")
      img.src = previewUrl
      img.alt = filename
      img.className = "h-10 w-10 object-cover rounded"
      info.append(img)
    }
    const text = document.createElement("div")
    text.className = "min-w-0 flex-1"
    const name = document.createElement("p")
    name.className = "text-sm font-medium text-gray-900 truncate"
    name.textContent = filename
    const size = document.createElement("p")
    size.className = "text-xs text-green-600"
    size.textContent = this.formatFileSize(byteSize)
    text.append(name, size)
    info.append(text)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "ml-2 p-1 text-gray-400 hover:text-red-500"
    remove.dataset.action = "click->file-upload#removeUpload"
    remove.dataset.uploadId = uploadId
    remove.innerHTML =
      '<svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>'

    line.append(info, remove)
    return line
  }

  // Remove a server-rendered staged upload row (rendered on a validation
  // re-render; the hidden signed_id input lives inside the row)
  removeStagedRow(event) {
    this.removeRow(event.currentTarget.closest("[data-staged-row]"))
  }

  // Remove error element
  removeErrorElement(event) {
    const uploadId = event.currentTarget.dataset.uploadId
    document.getElementById(uploadId)?.remove()
    this.updateEmptyState()
  }

  // Delete existing attachment (already saved to database)
  async deleteAttachment(event) {
    event.preventDefault()

    // Capture before awaiting — event.currentTarget becomes null after an await
    const button = event.currentTarget
    const attachmentId = button.dataset.deleteAttachmentId
    // Find the parent container with data-attachment-id
    const element = button.closest("[data-attachment-id]")

    if (!(await confirmDialog("Datei wirklich löschen?", { danger: true }))) return

    try {
      const response = await fetch(`/custom_report_attachments/${attachmentId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']")?.content,
          Accept: "application/json",
        },
      })

      if (response.ok) {
        this.removeRow(element)
      } else {
        alert("Fehler beim Löschen der Datei")
      }
    } catch (error) {
      console.error("Delete error:", error)
      alert("Fehler beim Löschen der Datei")
    }
  }

  // Update empty state visibility
  updateEmptyState() {
    if (!this.hasEmptyStateTarget) return

    const hasExisting = this.fileListTarget.children.length > 0
    const hasPending = this.pendingListTarget.children.length > 0

    if (hasExisting || hasPending) {
      this.emptyStateTarget.classList.add("hidden")
    } else {
      this.emptyStateTarget.classList.remove("hidden")
    }
  }

  // Helper: Format file size
  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  // Helper: Get file type icon
  getFileIcon(mimeType) {
    if (mimeType.startsWith("image/")) {
      return '<svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>'
    } else if (mimeType.startsWith("video/")) {
      return '<svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>'
    } else if (mimeType === "application/pdf") {
      return '<svg class="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>'
    } else if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) {
      return '<svg class="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" /></svg>'
    } else if (mimeType.includes("word") || mimeType.includes("document")) {
      return '<svg class="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>'
    } else {
      return '<svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>'
    }
  }

  // Open file dialog
  openFileDialog() {
    this.inputTarget.click()
  }
}
